/* eslint-disable max-len */
// src/components/ProjectOverview.js
// UPDATED: Dark Theme Redesign

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, collection, query, where, onSnapshot, getDocs, orderBy, updateDoc, serverTimestamp, limit } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from '../../firebaseConfig';
import {
    FiLink, FiTrash2, FiPlusCircle, FiImage, FiLoader,
    FiRefreshCw, FiPlay, FiPause,
    FiEdit2, FiSave, FiX, FiActivity, FiCheckCircle, FiWifi, FiWifiOff,
    FiChevronDown, FiChevronUp, FiCircle, FiArrowRight, FiUsers, FiFeather
} from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';

// Import child components
import ScheduledPostsList from './ScheduledPostsList';
import PostHistoryList from './PostHistoryList';
import ImageUploadModal from '../common/ImageUploadModal';
import EditPostModal from '../common/EditPostModal';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import { INDUSTRIES } from '../shared/constants/industries';
import { TAXONOMY_MIRROR } from '../shared/constants/taxonomyMirror';
import WorkflowDraftsCard from './WorkflowDraftsCard';

// --- Module-scope callable references ---
const checkLocalNodeStatusFn = httpsCallable(functions, 'checkLocalNodeStatus');
const linkMetaAccountFn = httpsCallable(functions, 'linkMetaAccount');
const linkLinkedInAccountFn = httpsCallable(functions, 'linkLinkedInAccount');
const deleteProjectFn = httpsCallable(functions, 'deleteProject');
const toggleStreamStatusFn = httpsCallable(functions, 'toggleStreamStatus');
const toggleProjectTeamSharingFn = httpsCallable(functions, 'toggleProjectTeamSharing');

// --- Constants ---
const tones = ["Professional", "Friendly", "Inspirational", "Playful", "Bold", "Casual", "Authoritative", "Witty"];

// --- Helper Components ---

// --- Helper Components ---

function StatusPill({ label, status, icon, subtext, onClick, className }) {
    const colors = {
        online: "bg-pastel-green-bg/30 text-pastel-green border-pastel-green/30",
        offline: "bg-soft-coral/10 text-soft-coral border-soft-coral/20",
        warning: "bg-amber-400/10 text-amber-400 border-amber-400/20", 
        active: "bg-slate-blue/10 text-slate-blue border-slate-blue/20",
        checking: "bg-cream/10 text-cream/60 border-cream/20 animate-pulse",
        default: "bg-white/5 text-cream/50 border-white/10"
    };
    
    const style = colors[status] || colors.default;

    return (
        <div 
            onClick={onClick}
            className={`flex items-center px-4 py-2 rounded-full border ${style} text-xs font-bold uppercase tracking-wider min-w-[100px] justify-center transition-all ${onClick ? 'cursor-pointer hover:scale-105 active:scale-95' : ''} ${className || ''}`}
        >
            {icon && <span className="mr-2 text-sm">{icon}</span>}
            <div className="flex flex-col leading-none items-center">
                <span>{label}</span>
                {subtext && <span className="text-[9px] opacity-70 mt-0.5 font-medium">{subtext}</span>}
            </div>
        </div>
    );
}

function SectionCard({ title, icon, action, children, className = '' }) {
    return (
        <div className={`bg-warm-gray/10 backdrop-blur-md rounded-[2.5rem] border border-white/5 overflow-hidden ${className}`}>
            <div className="px-8 py-6 border-b border-white/5 flex justify-between items-center bg-black/5">
                <h3 className="text-sm font-black text-cream/40 flex items-center uppercase tracking-widest">
                    {icon && <span className="mr-3 text-slate-blue">{icon}</span>}
                    {title}
                </h3>
                {action}
            </div>
            <div className="p-8">
                {children}
            </div>
        </div>
    );
}

// --- Extracted sub-components (must be at module scope for stable reconciliation) ---
const StreamBadge = ({ status }) => {
    const styles = {
        active: 'bg-pastel-green-bg/30 text-pastel-green border-pastel-green/30',
        paused: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
        error: 'bg-soft-coral/10 text-soft-coral border-soft-coral/20',
        completed: 'bg-white/5 text-cream/50 border-white/10'
    };
    return <span className={`px-2 py-0.5 inline-flex text-[10px] leading-4 font-bold rounded-full border uppercase tracking-wider ${styles[status] || styles.completed}`}>{status}</span>;
};


function ProjectChecklist({ steps, completedCount, total, storageKey }) {
    const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem(storageKey) === 'true');

    const toggleCollapse = () => {
        const next = !collapsed;
        localStorage.setItem(storageKey, String(next));
        setCollapsed(next);
    };

    return (
        <div className="bg-warm-gray/10 backdrop-blur-md rounded-[2.5rem] border border-slate-blue/20 overflow-hidden">
            <button onClick={toggleCollapse} className="w-full px-8 py-6 flex items-center justify-between gap-4 group">
                <div className="flex items-center gap-3 min-w-0">
                    <h3 className="text-sm font-black text-cream/40 uppercase tracking-widest">Getting Started</h3>
                    <span className="text-[10px] font-bold text-cream/30 bg-white/5 px-2 py-0.5 rounded-full">{completedCount}/{total}</span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-16 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-slate-blue rounded-full transition-all duration-500" style={{ width: `${(completedCount / total) * 100}%` }} />
                    </div>
                    {collapsed
                        ? <FiChevronDown className="h-4 w-4 text-cream/30 group-hover:text-cream/60 transition-colors" />
                        : <FiChevronUp className="h-4 w-4 text-cream/30 group-hover:text-cream/60 transition-colors" />
                    }
                </div>
            </button>

            {!collapsed && (
                <div className="px-8 pb-6 space-y-2">
                    {steps.map((step, idx) => {
                        const inner = (
                            <div className={`flex items-center gap-3 px-4 py-3 rounded-2xl border transition-all duration-200 group/step ${
                                step.done
                                    ? 'border-white/5 bg-white/5'
                                    : 'border-white/10 bg-warm-gray/20 hover:border-slate-blue/30 hover:bg-warm-gray/30'
                            }`}>
                                {step.done
                                    ? <FiCheckCircle className="h-4 w-4 text-slate-blue flex-shrink-0" />
                                    : <FiCircle className="h-4 w-4 text-cream/30 flex-shrink-0" />
                                }
                                <p className={`text-xs font-bold uppercase tracking-wide flex-1 ${
                                    step.done ? 'text-cream/40 line-through decoration-cream/20' : 'text-cream'
                                }`}>
                                    {step.label}
                                </p>
                                {!step.done && step.to && (
                                    <FiArrowRight className="h-3.5 w-3.5 text-cream/20 group-hover/step:text-slate-blue transition-colors flex-shrink-0" />
                                )}
                            </div>
                        );

                        if (step.done || !step.to) return <div key={idx}>{inner}</div>;
                        return <Link key={idx} to={step.to} className="block">{inner}</Link>;
                    })}
                </div>
            )}
        </div>
    );
}

// --- Main Component ---
function ProjectOverview() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();

    // --- State ---
    const [userId, setUserId] = useState(null);
    const [project, setProject] = useState(null);
    const [loadingProject, setLoadingProject] = useState(true);
    const [error, setError] = useState('');
    const [, setConnectionDetails] = useState(null);
    const [availableConnections, setAvailableConnections] = useState([]);
    const [loadingConnections, setLoadingConnections] = useState(false);
    const [selectedConnectionToLink, setSelectedConnectionToLink] = useState('');
    const [isLinking, setIsLinking] = useState(false);
    const [isDeletingProject, setIsDeletingProject] = useState(false);
    const [recurringStreams, setRecurringStreams] = useState([]);
    const [loadingStreams, setLoadingStreams] = useState(true);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [togglingStreamId, setTogglingStreamId] = useState(null);

    // --- New State: Local Node Health ---
    const [localNodeStatus, setLocalNodeStatus] = useState('checking'); 

    // --- New State for Editing Vitals & Automation ---
    const [isEditingVitals, setIsEditingVitals] = useState(false);
    const [tempVitals, setTempVitals] = useState({});
    const [updatingSettings, setUpdatingSettings] = useState(false);

    // --- Brand DNA editing state ---
    const [isEditingBrandDna, setIsEditingBrandDna] = useState(false);
    const [tempBrandDna, setTempBrandDna] = useState('');
    const [savingBrandDna, setSavingBrandDna] = useState(false);
    
    const [activeOutputTab, setActiveOutputTab] = useState(location.state?.tab || 'queue');
    const [hasPost, setHasPost] = useState(false);

    // --- Team sharing state ---
    const [userData, setUserData] = useState(null);
    const [togglingTeamShare, setTogglingTeamShare] = useState(false);
    
    // Derive recurringCampaigns from recurringStreams (avoids duplicate subscription)
    const recurringCampaigns = recurringStreams;
    const loadingCampaigns = loadingStreams;

    const isMountedRef = useRef(true);

    // --- Auto Painter next run time (schedule: "15 9,13,17,21 * * *" UTC) ---
    const nextAutoPainterRun = useMemo(() => {
        const now = new Date();
        const hours = [9, 13, 17, 21];
        const nextHour = hours.find(h => h > now.getUTCHours() || (h === now.getUTCHours() && now.getUTCMinutes() < 15));
        const result = new Date(now);
        if (nextHour === undefined) {
            result.setUTCDate(now.getUTCDate() + 1);
            result.setUTCHours(hours[0], 15, 0, 0);
        } else {
            result.setUTCHours(nextHour, 15, 0, 0);
        }
        return result;
    }, []);

    const nextRunLabel = useMemo(() => {
        const h = String(nextAutoPainterRun.getUTCHours()).padStart(2, '0');
        const m = String(nextAutoPainterRun.getUTCMinutes()).padStart(2, '0');
        return `${h}:${m} UTC`;
    }, [nextAutoPainterRun]);

    // --- Callbacks for actions ---
    const [editingPostData, setEditingPostData] = useState(null); 

    const handleOpenEditModal = useCallback((post, platform) => {
        setEditingPostData({ post, platform });
    }, []);
    const handleCloseEditModal = useCallback(() => setEditingPostData(null), []);

    // --- Health Check Handler ---
    const checkLocalNodeHealth = useCallback(async () => {
        if (!isMountedRef.current) return;
        setLocalNodeStatus('checking');
        try {
            const result = await checkLocalNodeStatusFn();
            if (isMountedRef.current) {
                setLocalNodeStatus(result.data.isOnline ? 'online' : 'offline');
            }
        } catch (err) {
            if (isMountedRef.current) setLocalNodeStatus('offline');
        }
    }, []);




    const fetchConnectionData = useCallback(async (currentProject, currentUserId) => {
        if (!currentProject || !currentUserId) return;

        const { platform, associatedPageId, associatedInstagramId, associatedLinkedInId } = currentProject;
        const isMetaPlatformLocal = platform === 'Facebook' || platform === 'Instagram' || platform === 'Meta (Facebook & Instagram)';

        if (isMountedRef.current) { setLoadingConnections(true); setSelectedConnectionToLink(''); }

        const isMetaLinkedLocal = !!associatedPageId || !!associatedInstagramId;
        const connectionsRef = collection(db, "users", currentUserId, "connections");
        const projectsRef = collection(db, "projects");
        const qProjects = query(projectsRef, where("userId", "==", currentUserId));

        try {
            // Fetch both Meta and LinkedIn connections in parallel
            const qMetaConns = query(connectionsRef, where("provider", "==", "meta"));
            const qLinkedInConns = query(connectionsRef, where("provider", "==", "linkedin"));
            const [metaSnap, linkedInSnap, projectsSnap] = await Promise.all([
                getDocs(qMetaConns), getDocs(qLinkedInConns), getDocs(qProjects),
            ]);
            if (!isMountedRef.current) return;

            const allMetaConnections = metaSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const allLinkedInConnections = linkedInSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            const linkedPageIdsInOtherProjects = new Set();
            const linkedIgIdsInOtherProjects = new Set();
            const linkedLinkedInIdsInOtherProjects = new Set();

            projectsSnap.forEach(projDoc => {
                if (projDoc.id !== projectId) {
                    const data = projDoc.data();
                    if (data.associatedPageId) linkedPageIdsInOtherProjects.add(data.associatedPageId);
                    if (data.associatedInstagramId) linkedIgIdsInOtherProjects.add(data.associatedInstagramId);
                    if (data.associatedLinkedInId) linkedLinkedInIdsInOtherProjects.add(data.associatedLinkedInId);
                }
            });

            const available = [];

            // Meta connections
            if (isMetaPlatformLocal && isMetaLinkedLocal) {
                const linkedConn = allMetaConnections.find(c => c.pageId === associatedPageId) ||
                                   allMetaConnections.find(c => c.instagramAccountId === associatedInstagramId);
                setConnectionDetails(linkedConn || {
                    pageName: associatedPageId ? `Page ID: ${associatedPageId}` : 'No FB',
                    instagramUsername: associatedInstagramId ? `IG ID: ${associatedInstagramId}` : 'No IG'
                });
            } else if (isMetaPlatformLocal) {
                const metaAvailable = allMetaConnections.filter(conn => {
                     const isPageLinkedElsewhere = linkedPageIdsInOtherProjects.has(conn.pageId);
                     const isIgLinkedElsewhere = conn.instagramAccountId && linkedIgIdsInOtherProjects.has(conn.instagramAccountId);

                     if (platform === 'Meta (Facebook & Instagram)') return !isPageLinkedElsewhere;
                     if (platform === 'Facebook') return !isPageLinkedElsewhere;
                     if (platform === 'Instagram') return conn.instagramAccountId && !isIgLinkedElsewhere;
                     return false;
                }).map(conn => {
                     let label = `FB: ${conn.pageName}`;
                     if (conn.instagramUsername) label += ` + IG: @${conn.instagramUsername}`;
                     return { value: `meta_${conn.pageId}`, label };
                });
                available.push(...metaAvailable);
                setConnectionDetails(null);
            }

            // LinkedIn connections (always shown — LinkedIn can be linked alongside or independently)
            if (!associatedLinkedInId) {
                const linkedInAvailable = allLinkedInConnections.filter(conn => {
                    return conn.id && !linkedLinkedInIdsInOtherProjects.has(conn.id);
                }).map(conn => ({
                    value: `linkedin_${conn.id}`,
                    label: `LinkedIn: ${conn.displayName || conn.name || conn.id}`,
                }));
                available.push(...linkedInAvailable);
            }

            setAvailableConnections(available);
        } catch (err) {
            if (isMountedRef.current) setError("Could not load connection details.");
        } finally {
            if (isMountedRef.current) setLoadingConnections(false);
        }
    }, [projectId]);

    const handleLinkAccount = useCallback(async () => {
        if (!selectedConnectionToLink || !project) return;
        setIsLinking(true); setError('');

        const isLinkedInSelection = selectedConnectionToLink.startsWith('linkedin_');

        try {
            if (isLinkedInSelection) {
                // LinkedIn linking — connection doc ID is after the prefix
                const linkedInConnectionId = selectedConnectionToLink.replace('linkedin_', '');
                await linkLinkedInAccountFn({ projectId, linkedInConnectionId });
            } else {
                // Meta linking
                const parts = selectedConnectionToLink.split('_');
                const payload = { projectId, platform: project.platform };

                if (parts[0] === 'meta' || parts[0] === 'fb') {
                    payload.targetPageId = parts[1];
                } else if (parts[0] === 'ig') {
                    payload.targetPageId = parts[1];
                    payload.targetInstagramId = parts[2];
                } else {
                    setError("Invalid connection selection."); setIsLinking(false); return;
                }

                await linkMetaAccountFn(payload);
            }

            if (isMountedRef.current) { setSelectedConnectionToLink(''); addToast('Account linked successfully!', 'success'); }
        } catch (err) {
            if (isMountedRef.current) setError("Account linking failed. Please try again.");
            addToast("Account linking failed. Please try again.", 'error');
        } finally {
            if (isMountedRef.current) setIsLinking(false);
        }
    }, [selectedConnectionToLink, project, projectId, addToast]);

    const handleDeleteProject = useCallback(async () => {
        if (!project) return;
        const confirmed = await confirm({ title: 'Delete Project', message: `Are you sure you want to delete "${project.projectName}"? This cannot be undone.`, confirmLabel: 'Delete', variant: 'danger' });
        if (!confirmed) return;
        
        setIsDeletingProject(true);
        try {
            await deleteProjectFn({ projectId });
            navigate('/projects', { replace: true, state: { message: `Project "${project.projectName}" deleted.` } });
        } catch (err) {
            if (isMountedRef.current) {
                setError("Project deletion failed. Please try again.");
                addToast("Project deletion failed. Please try again.", 'error');
                setIsDeletingProject(false);
            }
        }
    }, [project, projectId, confirm, navigate, addToast]);

    const handleToggleStreamStatus = useCallback(async (streamId) => {
        if (!projectId || !streamId) return;
        const stream = recurringStreams.find(s => s.id === streamId);
        if (!stream) return;

        setTogglingStreamId(streamId); setError('');

        try {
            const result = await toggleStreamStatusFn({ projectId, streamId });
            const newStatus = result.data.newStatus;
            addToast(`Campaign '${stream.streamName}' is now ${newStatus}.`, 'success');
        } catch (err) {
            if (isMountedRef.current) { setError("Stream toggle failed. Please try again."); }
            addToast("Stream toggle failed. Please try again.", 'error');
        } finally {
            if (isMountedRef.current) { setTogglingStreamId(null); }
        }
    }, [projectId, addToast, recurringStreams]);

    // --- Handlers for Vitals Editing & Automation ---

    const handleEditVitals = () => {
        const currentSettings = project.defaultVisualSettings || {};
        setTempVitals({
            industry: project.industry || "",
            brandVoiceTone: project.brandVoiceTone,
            categoryId: currentSettings.categoryId || TAXONOMY_MIRROR[0].id,
            vibeId: currentSettings.vibeId || TAXONOMY_MIRROR[0].vibes[0].id
        });
        setIsEditingVitals(true);
    };

    const handleSaveVitals = async () => {
        setUpdatingSettings(true);
        try {
            const projectRef = doc(db, "projects", projectId);
            const newVisualSettings = {
                categoryId: tempVitals.categoryId,
                vibeId: tempVitals.vibeId,
                textStrategy: "auto",
                style: `${tempVitals.categoryId} - ${tempVitals.vibeId}`
            };
            await updateDoc(projectRef, {
                industry: tempVitals.industry,
                brandVoiceTone: tempVitals.brandVoiceTone,
                defaultVisualSettings: newVisualSettings,
                defaultVisualStyle: newVisualSettings.style, 
                updatedAt: serverTimestamp()
            });
            setIsEditingVitals(false);
            addToast('Project settings updated!', 'success');
        } catch (err) {
            addToast('Failed to save settings.', 'error');
        } finally {
            setUpdatingSettings(false);
        }
    };


    const handleEditBrandDna = () => {
        setTempBrandDna(project.aiFullDescription || '');
        setIsEditingBrandDna(true);
    };

    const handleSaveBrandDna = async () => {
        if (!tempBrandDna.trim() || tempBrandDna.trim().length < 50) {
            addToast('Brand DNA must be at least 50 characters.', 'warning');
            return;
        }
        setSavingBrandDna(true);
        try {
            const projectRef = doc(db, "projects", projectId);
            await updateDoc(projectRef, {
                aiFullDescription: tempBrandDna.trim(),
                updatedAt: serverTimestamp()
            });
            setIsEditingBrandDna(false);
            addToast('Brand DNA updated!', 'success');
        } catch (err) {
            addToast('Failed to save Brand DNA.', 'error');
        } finally {
            setSavingBrandDna(false);
        }
    };

    // --- useEffect Hooks ---
    useEffect(() => {
        isMountedRef.current = true;
        checkLocalNodeHealth();
        return () => { isMountedRef.current = false; };
    }, [checkLocalNodeHealth]);

    useEffect(() => {
        setLoadingProject(true);
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                const currentUserId = user.uid;
                setUserId(currentUserId);
                const projectRef = doc(db, "projects", projectId);
                const unsubscribeProject = onSnapshot(projectRef, (docSnap) => {
                    if (!isMountedRef.current) return;
                    if (docSnap.exists() && docSnap.data().userId === currentUserId) {
                        const projectData = { id: docSnap.id, ...docSnap.data() };
                        setProject(projectData);
                        fetchConnectionData(projectData, currentUserId);
                        setError('');
                    } else {
                        setError("Project not found.");
                        setProject(null);
                    }
                    setLoadingProject(false);
                }, (err) => {
                    if (isMountedRef.current) { setError("Failed to load project."); setProject(null); setLoadingProject(false); }
                });
                const streamsRef = collection(db, "projects", projectId, "contentStreams");
                const qStreams = query(streamsRef, where("userId", "==", currentUserId), orderBy("createdAt", "desc"));
                const unsubscribeStreams = onSnapshot(qStreams, (snapshot) => {
                    if (isMountedRef.current) setRecurringStreams(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    setLoadingStreams(false);
                }, () => {
                    if (isMountedRef.current) { setLoadingStreams(false); }
                });
                const qPosts = query(collection(db, "projects", projectId, "posts"), where("status", "==", "posted"), limit(1));
                const unsubscribePosts = onSnapshot(qPosts, (snapshot) => {
                    if (isMountedRef.current) setHasPost(!snapshot.empty);
                }, () => {});
                return () => { unsubscribeProject(); unsubscribeStreams(); unsubscribePosts(); };
            } else {
                setUserId(null); setError("Authentication error."); setLoadingProject(false); setProject(null);
            }
        });
        return () => unsubscribeAuth();
    }, [projectId, fetchConnectionData]);

    // --- Fetch user doc for team data ---
    useEffect(() => {
        if (!userId) return;
        const userRef = doc(db, "users", userId);
        const unsubscribeUser = onSnapshot(userRef, (snap) => {
            if (snap.exists() && isMountedRef.current) {
                setUserData(snap.data());
            }
        }, () => {});
        return () => unsubscribeUser();
    }, [userId]);

    // --- Team sharing toggle handler ---
    const handleToggleTeamSharing = useCallback(async (shared) => {
        if (!projectId) return;
        setTogglingTeamShare(true);
        try {
            await toggleProjectTeamSharingFn({ projectId, shared });
            addToast(shared ? 'Project shared with team.' : 'Project unshared from team.', 'success');
        } catch (err) {
            addToast('Failed to update team sharing. Please try again.', 'error');
        } finally {
            if (isMountedRef.current) setTogglingTeamShare(false);
        }
    }, [projectId, addToast]);

    // --- Derived: team owner check ---
    const isTeamOwner = !!(userData?.teamId && userData?.teamRole === 'owner');
    const teamShared = !!project?.teamShared;

    // --- Render Logic ---
    if (loadingProject && !project) return <div className="flex justify-center items-center h-screen"><FiLoader className="animate-spin text-4xl text-slate-blue" /></div>;
    if (error && !project) return <div className="p-8 text-center text-red-500 font-bold">{error}</div>;
    if (!project) return <div className="text-center p-10 text-gray-500">Project not found.</div>;

    const isMetaPlatform = project.platform === 'Facebook' || project.platform === 'Instagram' || project.platform === 'Meta (Facebook & Instagram)';
    const isMetaLinked = !!project.associatedPageId || !!project.associatedInstagramId;
    const isLinkedInLinked = !!project.associatedLinkedInId;
    const isProjectLinked = isMetaLinked || isLinkedInLinked;
    const activeCategoryVibes = TAXONOMY_MIRROR.find(c => c.id === tempVitals.categoryId)?.vibes || [];
    
    const vitalsActionButtons = isEditingVitals ? (
        <div className="flex gap-2">
            <button onClick={handleSaveVitals} disabled={updatingSettings} className="p-2 text-pastel-green hover:bg-pastel-green-bg/20 rounded-full transition-colors" title="Save">
                {updatingSettings ? <FiLoader className="animate-spin"/> : <FiSave />}
            </button>
            <button onClick={() => setIsEditingVitals(false)} disabled={updatingSettings} className="p-2 text-soft-coral hover:bg-soft-coral/10 rounded-full transition-colors" title="Cancel">
                <FiX />
            </button>
        </div>
    ) : (
        <button onClick={handleEditVitals} className="p-2 text-cream/50 hover:bg-white/10 hover:text-cream rounded-full transition-colors" title="Edit Vitals">
            <FiEdit2 />
        </button>
    );

    return (
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12 py-10 pb-24 space-y-12">
            {editingPostData && (
                <EditPostModal 
                 project={project} 
                 post={editingPostData.post} 
                initialPlatform={editingPostData.platform} 
                 onClose={handleCloseEditModal}
              />
            )}
            {isImageModalOpen && <ImageUploadModal userId={userId} projectId={projectId} currentImageUrl={project.coverImageUrl} onClose={() => setIsImageModalOpen(false)} />}
            
            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
                <div>
                     <div className="flex items-center gap-3 mb-2">
                        {/* Breadcrumb removed per user feedback */}
                        <h1 className="text-4xl font-black text-cream uppercase tracking-tight">{project.projectName}</h1>
                     </div>
                     <p className="text-cream/60 text-lg font-medium">{project.platform} • {project.industry}</p>
                     {isTeamOwner && (
                         <div className="flex items-center gap-3 mt-3">
                             <FiUsers className="text-cream/40" size={14} />
                             <span className="text-xs font-medium text-cream/40">Share with team</span>
                             <button
                                 onClick={() => handleToggleTeamSharing(!teamShared)}
                                 disabled={togglingTeamShare}
                                 className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                                     teamShared ? 'bg-slate-blue' : 'bg-white/10'
                                 } ${togglingTeamShare ? 'opacity-50' : ''}`}
                                 role="switch"
                                 aria-checked={teamShared}
                             >
                                 <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform transition duration-200 ${
                                     teamShared ? 'translate-x-4' : 'translate-x-0'
                                 }`} />
                             </button>
                         </div>
                     )}
                </div>

                <div className="flex flex-wrap items-center gap-4">
                     {isMetaLinked ? (
                         <StatusPill status="online" icon={<FiCheckCircle/>} label={project.platform === 'Instagram' ? 'Instagram' : 'Meta'} subtext="Linked"/>
                     ) : isMetaPlatform ? (
                         <StatusPill status="warning" icon={<FiLink/>} label="Meta" subtext="Not Linked"/>
                     ) : null}

                     {isLinkedInLinked ? (
                         <StatusPill status="online" icon={<FaLinkedin/>} label="LinkedIn" subtext="Linked"/>
                     ) : (
                         <StatusPill status="warning" icon={<FaLinkedin/>} label="LinkedIn" subtext="Not Linked"/>
                     )}

                     <StatusPill 
                         onClick={checkLocalNodeHealth}
                         status={localNodeStatus} 
                         icon={localNodeStatus === 'online' ? <FiWifi/> : <FiWifiOff/>} 
                         label={localNodeStatus === 'checking' ? 'Checking' : 'Local AI'}
                         subtext={localNodeStatus === 'online' ? 'Online' : 'Offline'}
                     />

                     <StatusPill
                         status='active'
                         icon={<FiActivity/>}
                         label="Auto Painter"
                         subtext={`Next: ${nextRunLabel}`}
                     />

                    <div className="h-10 w-px bg-white/10 mx-2 hidden sm:block"></div>

                    <button
                        onClick={() => navigate(`/project/${projectId}/content-hub`)}
                        disabled={!isProjectLinked}
                        className="btn btn-primary px-6 py-3 text-sm shadow-glow hover:shadow-glow/80"
                    >
                        <FiPlusCircle className="mr-2 h-5 w-5" /> Create Content
                    </button>
                </div>
            </div>

            {error && <div className="p-6 bg-soft-coral/10 border border-soft-coral/20 text-soft-coral rounded-3xl text-sm font-bold shadow-lg">{error}</div>}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                
                {/* --- LEFT COLUMN: STRATEGY (4 cols) --- */}
                <aside className="lg:col-span-4 space-y-6">
                    
                    {/* 1. Identity Card */}
                    <div className="bg-warm-gray/10 backdrop-blur-md rounded-[2.5rem] border border-white/5 overflow-hidden shadow-xl">
                         <div className="relative h-48 bg-warm-dark group border-b border-light-border">
                            {project.coverImageUrl ? (
                                <img src={project.coverImageUrl} alt="Cover" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-all duration-700"/>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-cream/10 bg-warm-gray/20">
                                    <FiImage className="h-10 w-10"/>
                                </div>
                            )}
                            <button 
                                onClick={() => userId && projectId ? setIsImageModalOpen(true) : null}
                                className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity text-cream text-xs font-bold uppercase tracking-widest backdrop-blur-sm"
                            >
                                <FiImage className="mr-2"/> Change Cover
                            </button>
                         </div>
                         
                         <div className="p-8">
                            <div className="flex justify-between items-start mb-8">
                                <h3 className="text-sm font-black text-cream/40 uppercase tracking-widest flex items-center">
                                    <FiActivity className="mr-3 h-4 w-4 text-slate-blue"/> Project Vitals
                                </h3>
                                {vitalsActionButtons}
                            </div>
                            
                            {isEditingVitals ? (
                                <div className="space-y-5 bg-warm-dark/50 p-6 rounded-3xl border border-white/5 shadow-inner">
                                    <div className="relative">
                                        <label className="form-label text-cream/60">Industry</label>
                                        <input
                                            type="text"
                                            value={tempVitals.industry || ''}
                                            onChange={(e) => setTempVitals({...tempVitals, industry: e.target.value, _industryOpen: true})}
                                            onFocus={() => setTempVitals(v => ({...v, _industryOpen: true}))}
                                            placeholder="Type to search..."
                                            className="form-input text-xs py-3 rounded-xl bg-warm-gray/20 text-cream w-full"
                                        />
                                        {tempVitals._industryOpen && tempVitals.industry && (
                                            <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-warm-gray border border-white/10 rounded-xl shadow-modal">
                                                {INDUSTRIES.filter(i => i.toLowerCase().includes((tempVitals.industry || '').toLowerCase())).map((ind) => (
                                                    <button
                                                        key={ind}
                                                        type="button"
                                                        onClick={() => setTempVitals({...tempVitals, industry: ind, _industryOpen: false})}
                                                        className="w-full text-left px-4 py-2 text-xs text-cream/80 hover:bg-slate-blue/15 hover:text-cream transition-colors"
                                                    >
                                                        {ind}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="form-label text-cream/60">Voice</label>
                                        <select 
                                            value={tempVitals.brandVoiceTone} 
                                            onChange={(e) => setTempVitals({...tempVitals, brandVoiceTone: e.target.value})}
                                            className="form-select text-xs py-3 rounded-xl bg-warm-gray/20 text-cream"
                                        >
                                            {tones.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label text-cream/60">Visual Style</label>
                                        <div className="flex gap-3">
                                            <select 
                                                value={tempVitals.categoryId} 
                                                onChange={(e) => {
                                                    const cat = TAXONOMY_MIRROR.find(c => c.id === e.target.value);
                                                    setTempVitals({
                                                        ...tempVitals, 
                                                        categoryId: e.target.value,
                                                        vibeId: cat ? cat.vibes[0].id : ''
                                                    });
                                                }}
                                                className="form-select text-xs py-3 w-1/2 rounded-xl bg-warm-gray/20 text-cream"
                                            >
                                                {TAXONOMY_MIRROR.map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                                            </select>
                                            <select 
                                                value={tempVitals.vibeId} 
                                                onChange={(e) => setTempVitals({...tempVitals, vibeId: e.target.value})}
                                                className="form-select text-xs py-3 w-1/2 rounded-xl bg-warm-gray/20 text-cream"
                                            >
                                                {activeCategoryVibes.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4 text-sm text-cream/80">
                                    <div className="flex justify-between border-b border-white/5 pb-3">
                                        <span className="text-cream/40 font-bold uppercase text-xs">Platform</span>
                                        <span className="font-medium truncate pl-2">{project.platform}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-white/5 pb-3">
                                        <span className="text-cream/40 font-bold uppercase text-xs">Industry</span>
                                        <span className="font-medium">{project.industry}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-white/5 pb-3">
                                        <span className="text-cream/40 font-bold uppercase text-xs">Voice</span>
                                        <span className="font-medium">{project.brandVoiceTone}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-cream/40 font-bold uppercase text-xs">Style</span>
                                        <span className="font-medium">
                                            {project.defaultVisualSettings
                                                ? `${(project.defaultVisualSettings.categoryId || '').replace(/^\w/, c => c.toUpperCase())} — ${(project.defaultVisualSettings.vibeId || '').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())}`
                                                : (project.defaultVisualStyle || 'General')}
                                        </span>
                                    </div>

                                    {/* Connection CTA — visible when accounts can still be linked */}
                                    {(!isMetaLinked || !isLinkedInLinked) && (
                                        <div className="mt-6 p-4 rounded-2xl border border-amber-400/20 bg-amber-400/5">
                                            <p className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-3 flex items-center">
                                                <FiLink className="mr-2 h-3.5 w-3.5"/> Connect Account
                                            </p>
                                            {isLinkedInLinked && <p className="text-[10px] text-[#0077B5] mb-2 font-bold flex items-center"><FaLinkedin className="mr-1"/> LinkedIn: {project.associatedLinkedInName || 'Linked'}</p>}
                                            {isMetaLinked && <p className="text-[10px] text-blue-400 mb-2 font-bold flex items-center"><FiCheckCircle className="mr-1"/> Meta: Linked</p>}
                                            {loadingConnections ? <div className="text-xs italic text-cream/30">Loading...</div> : (
                                                availableConnections.length > 0 ? (
                                                    <div className="flex gap-2">
                                                        <select
                                                            value={selectedConnectionToLink}
                                                            onChange={(e) => setSelectedConnectionToLink(e.target.value)}
                                                            disabled={isLinking}
                                                            className="form-select text-xs py-2 rounded-xl bg-warm-gray/20 text-cream flex-1"
                                                        >
                                                            <option value="" disabled>Select Account...</option>
                                                            {availableConnections.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                                        </select>
                                                        <button onClick={handleLinkAccount} disabled={isLinking || !selectedConnectionToLink} className="btn btn-primary px-4 py-2 text-xs">
                                                            {isLinking ? '...' : 'Link'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-cream/50">No available accounts. <Link to="/settings" className="text-slate-blue hover:underline">Go to Settings</Link>.</p>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                         </div>
                    </div>



                    {/* Brand DNA Card */}
                    <div className="bg-warm-gray/10 backdrop-blur-md rounded-[2.5rem] border border-white/5 overflow-hidden shadow-xl p-8">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-sm font-black text-cream/40 uppercase tracking-widest flex items-center">
                                <FiFeather className="mr-3 h-4 w-4 text-slate-blue"/> Brand DNA
                            </h3>
                            {isEditingBrandDna ? (
                                <div className="flex gap-2">
                                    <button onClick={handleSaveBrandDna} disabled={savingBrandDna} className="p-2 text-pastel-green hover:bg-pastel-green-bg/20 rounded-full transition-colors" title="Save">
                                        {savingBrandDna ? <FiLoader className="animate-spin"/> : <FiSave />}
                                    </button>
                                    <button onClick={() => setIsEditingBrandDna(false)} disabled={savingBrandDna} className="p-2 text-soft-coral hover:bg-soft-coral/10 rounded-full transition-colors" title="Cancel">
                                        <FiX />
                                    </button>
                                </div>
                            ) : (
                                <button onClick={handleEditBrandDna} className="p-2 text-cream/50 hover:bg-white/10 hover:text-cream rounded-full transition-colors" title="Edit Brand DNA">
                                    <FiEdit2 />
                                </button>
                            )}
                        </div>
                        <p className="text-xs text-cream/30 mb-4">This description drives all AI-generated content for this project.</p>
                        {isEditingBrandDna ? (
                            <textarea
                                value={tempBrandDna}
                                onChange={(e) => setTempBrandDna(e.target.value)}
                                rows={6}
                                className="w-full bg-warm-dark/50 border border-white/5 rounded-2xl text-cream text-sm p-4 focus:border-slate-blue focus:ring-1 focus:ring-slate-blue resize-y leading-relaxed"
                                placeholder="Describe the brand identity, audience, value proposition, and aesthetic direction..."
                            />
                        ) : (
                            <p className="text-sm text-cream/70 leading-relaxed">{project.aiFullDescription || <span className="italic text-cream/30">No Brand DNA set.</span>}</p>
                        )}
                    </div>

                     {/* 4. Danger Zone */}
                     <div className="pt-6 mt-10 border-t border-white/5">
                         <button 
                            onClick={handleDeleteProject} 
                            disabled={isDeletingProject}
                            className="text-xs text-soft-coral hover:text-red-400 flex items-center hover:underline disabled:opacity-50 font-bold uppercase tracking-wide"
                         >
                            <FiTrash2 className="mr-2"/> 
                            {isDeletingProject ? 'Deleting...' : 'Delete Project'}
                         </button>
                     </div>

                </aside>

                {/* --- RIGHT COLUMN: PRODUCTION FLOOR (8 cols) --- */}
                <main className="lg:col-span-8 space-y-8">

                    {/* Getting Started Checklist */}
                    {(() => {
                        const checklistSteps = [
                            { done: isProjectLinked, label: 'Link your Meta page', to: null },
                            { done: hasPost, label: 'Create & publish a post', to: `/project/${projectId}/content-hub?workflow=single` },
                            { done: recurringStreams.length > 0, label: 'Create a content stream', to: `/project/${projectId}/content-hub?workflow=stream` },
                        ];
                        const allDone = checklistSteps.every(s => s.done);
                        if (allDone) return null;

                        const CHECKLIST_KEY = `nextai_project_checklist_collapsed_${projectId}`;
                        const completedCount = checklistSteps.filter(s => s.done).length;

                        return (
                            <ProjectChecklist
                                steps={checklistSteps}
                                completedCount={completedCount}
                                total={checklistSteps.length}
                                storageKey={CHECKLIST_KEY}
                            />
                        );
                    })()}

                    {/* Active Campaigns */}
                    <SectionCard title="Active Campaigns" icon={<FiRefreshCw className="h-4 w-4"/>}>
                        {loadingStreams && <p className="text-sm text-cream/30 italic">Loading...</p>}
                        {!loadingStreams && recurringStreams.length === 0 && <p className="text-sm text-cream/50 italic">No active campaigns. Start one in the Content Hub.</p>}
                        {!loadingStreams && recurringStreams.length > 0 && (
                            <ul className="divide-y divide-white/5">
                                {recurringStreams.map(stream => (
                                    <li key={stream.id} className="py-5 flex items-center justify-between first:pt-0 last:pb-0">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-3">
                                                <Link to={`/project/${projectId}/stream/${stream.id}`} className="text-sm font-bold text-cream hover:text-slate-blue truncate uppercase tracking-tight transition-colors">
                                                    {stream.streamName || "Untitled Campaign"}
                                                </Link>
                                                <StreamBadge status={stream.status} />
                                            </div>
                                            <p className="text-xs text-cream/40 mt-1 truncate">
                                                {stream.frequency} • {stream.contentType} • {stream.topic}
                                            </p>
                                            {stream.nextGenerationDate && (() => {
                                                const batchSizes = { daily: 7, every_second_day: 4, weekly: 4, monthly: 3 };
                                                const batchSize = batchSizes[stream.frequency] || 4;
                                                const nextDate = stream.nextGenerationDate?.toDate ? stream.nextGenerationDate.toDate() : new Date(stream.nextGenerationDate);
                                                const now = new Date();
                                                const isToday = nextDate.toDateString() === now.toDateString();
                                                const timeStr = nextDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                                const dateStr = nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                                return isToday ? (
                                                    <p className="text-xs mt-1.5 text-cream/40">
                                                        Generating <span className="text-cream font-bold">{batchSize} new posts</span> today around{' '}
                                                        <span className="text-slate-blue font-bold">{timeStr}</span>
                                                    </p>
                                                ) : (
                                                    <p className="text-xs mt-1.5 text-cream/40">
                                                        On <span className="text-slate-blue font-bold">{dateStr}</span>
                                                        {', '}
                                                        <span className="text-cream font-bold">{batchSize} new posts</span>
                                                        {' will be generated'}
                                                    </p>
                                                );
                                            })()}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button 
                                                onClick={() => handleToggleStreamStatus(stream.id)}
                                                disabled={togglingStreamId === stream.id}
                                                className={`p-2 rounded-xl transition-colors ${
                                                    stream.status === 'active' 
                                                        ? 'text-amber-400 hover:bg-amber-400/10' 
                                                        : 'text-pastel-green hover:bg-pastel-green-bg/20'
                                                }`}
                                                title={stream.status === 'active' ? 'Pause' : 'Resume'}
                                            >
                                                {togglingStreamId === stream.id ? <FiLoader className="animate-spin"/> : (stream.status === 'active' ? <FiPause/> : <FiPlay/>)}
                                            </button>
                                            <Link 
                                                to={`/project/${projectId}/stream/${stream.id}`}
                                                className="p-2 text-cream/30 hover:text-cream hover:bg-white/5 rounded-xl transition-colors"
                                            >
                                                <FiEdit2/>
                                            </Link>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </SectionCard>
                    
                    {/* 1.5 In Progress Workflow Drafts */}
                    <WorkflowDraftsCard projectId={projectId} />

                    {/* 2. Output Feed (Output Logic: Scheduled + History) */}
                    <div className="bg-warm-gray/10 backdrop-blur-md rounded-[2.5rem] shadow-card border border-white/5 overflow-hidden min-h-[500px]">
                        <div className="flex border-b border-light-border">
                            <button
                                onClick={() => setActiveOutputTab('queue')}
                                className={`flex-1 py-5 text-sm font-black uppercase tracking-widest transition-colors ${
                                    activeOutputTab === 'queue'
                                        ? 'bg-warm-gray/20 text-slate-blue border-b-2 border-slate-blue'
                                        : 'bg-transparent text-cream/40 hover:text-cream hover:bg-white/5'
                                }`}
                            >
                                Queue
                            </button>
                            <button
                                onClick={() => setActiveOutputTab('history')}
                                className={`flex-1 py-5 text-sm font-black uppercase tracking-widest transition-colors ${
                                    activeOutputTab === 'history'
                                        ? 'bg-warm-gray/20 text-slate-blue border-b-2 border-slate-blue'
                                        : 'bg-transparent text-cream/40 hover:text-cream hover:bg-white/5'
                                }`}
                            >
                                History
                            </button>
                        </div>

                        <div className="p-8">
                            {activeOutputTab === 'queue' ? (
                                <ScheduledPostsList 
                                    userId={userId}
                                    projectId={projectId} 
                                    recurringCampaigns={recurringCampaigns}
                                    loadingCampaigns={loadingCampaigns}
                                    onOpenEditModal={handleOpenEditModal} 
                                />
                            ) : (
                                <PostHistoryList 
                                    userId={userId}
                                    projectId={projectId} 
                                    onRetryPost={handleOpenEditModal}
                                />
                            )}
                        </div>
                    </div>

                </main>
            </div>
        </div>
    );
}

export default ProjectOverview;
